import { useMemo, useState } from 'react';
import { Input, Tag as AntTag, Tooltip, Typography } from 'antd';
import {
  FileOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import styled, { css } from 'styled-components';
import TagFilter from './TagFilter';
import {
  collectTags,
  getEnabledTags,
  getTagColor,
  tagFilterMatches,
} from './tagFilterUtils';
import type { ITreeNode } from './types';

const { Text } = Typography;
const { Search } = Input;

/**
 * Multi-root Explorer: renders whatever `nodes: ITreeNode[]` the host hands
 * it — already a full nested tree (see resolve.ts / PLAN Faza 2, "Explorer
 * prima isključivo nodes: ITreeNode[]"). No eCTD-specific concepts here (no
 * lifecycle/seq/lineage/case-list) — only the generic `tags`/`counter`/`info`
 * decorations plus folder/file hierarchy.
 */

export interface IFileMenuAnchor {
  file: ITreeNode;
  x: number;
  y: number;
}

interface IExplorerProps {
  nodes: ITreeNode[];
  /** File ids currently open, mapped to their pair role for the row tag. */
  openRoles: Map<string, 'a' | 'b' | 'ref'>;
  /** File id selected via "Select for Compare". */
  compareSelectId: string | null;
  /** Whether folder rows start expanded or collapsed. Default `true`. */
  defaultExpanded?: boolean;
  /** Show a tag toggle bar under the search. Default `false`. */
  tagFilterEnabled?: boolean;
  tagFilterShowMoreText?: string;
  tagFilterShowLessText?: string;
  onOpenToSide: (file: ITreeNode) => void;
  onContextMenu: (anchor: IFileMenuAnchor) => void;
  onDragFile: (fileId: string) => void;
}

const matchFile = (node: ITreeNode, q: string) =>
  node.name.toLowerCase().includes(q);

const subtreeMatches = (nodes: ITreeNode[], q: string): boolean =>
  !q ||
  nodes.some(
    n => matchFile(n, q) || (n.children ? subtreeMatches(n.children, q) : false)
  );

const MAX_TAGS_PER_LINE = 2;
const TAG_HEIGHT = '22px';

interface IRowCtx {
  query: string;
  openRoles: Map<string, 'a' | 'b' | 'ref'>;
  compareSelectId: string | null;
  defaultExpanded: boolean;
  enabledTags: Set<string> | null;
  onOpenToSide: (file: ITreeNode) => void;
  onContextMenu: (a: IFileMenuAnchor) => void;
  onDragFile: (fileId: string) => void;
}

const RowExtras: React.FC<{ node: ITreeNode }> = ({ node }) => {
  // `true` → computed here from `children` (already at hand); a number/string
  // overrides with an explicit value (custom count, or arbitrary text).
  const counter =
    typeof node.counter === 'number' || typeof node.counter === 'string'
      ? node.counter
      : node.counter && node.children
        ? node.children.length
        : undefined;
  const tags = node.tags ?? [];

  return (
    <>
      {tags.length > 0 && (
        <Tags>
          {Array.from(
            { length: Math.ceil(tags.length / MAX_TAGS_PER_LINE) },
            (_, row) => (
              <TagsLine key={row}>
                {tags
                  .slice(row * MAX_TAGS_PER_LINE, (row + 1) * MAX_TAGS_PER_LINE)
                  .map((tag, index) => (
                    <AntTag
                      key={`${tag.label}-${index}`}
                      color={getTagColor(tag)}
                    >
                      {tag.label}
                    </AntTag>
                  ))}
              </TagsLine>
            )
          )}
        </Tags>
      )}
      {counter !== undefined && <Counter>{counter}</Counter>}
      {node.info && (
        <Tooltip title={node.info}>
          <InfoCircleOutlined style={{ opacity: 0.55 }} />
        </Tooltip>
      )}
    </>
  );
};

const NodeRow: React.FC<{ node: ITreeNode; ctx: IRowCtx }> = ({
  node,
  ctx,
}) => {
  const [open, setOpen] = useState(ctx.defaultExpanded);
  const isFolder = !!node.children;

  if (ctx.query) {
    if (
      isFolder
        ? !subtreeMatches(node.children!, ctx.query)
        : !matchFile(node, ctx.query)
    ) {
      return null;
    }
  }
  if (!tagFilterMatches(node, ctx.enabledTags)) return null;

  const isOpen = open || !!ctx.query;

  if (isFolder) {
    return (
      <div>
        <Row onClick={() => setOpen(o => !o)}>
          <RowLead>
            <Caret $open={isOpen}>
              {node.children!.length ? <RightOutlined /> : null}
            </Caret>
            <FolderOutlined />
            <RowName ellipsis={{ tooltip: node.name }}>{node.name}</RowName>
          </RowLead>
          <RowExtras node={node} />
        </Row>
        {isOpen && (
          <Children>
            {node.children!.map(c => (
              <NodeRow key={c.id} node={c} ctx={ctx} />
            ))}
          </Children>
        )}
      </div>
    );
  }

  const role = node.fileId ? ctx.openRoles.get(node.fileId) : undefined;
  const selected = !!node.fileId && ctx.compareSelectId === node.fileId;

  return (
    <FileRow
      $role={role}
      $selected={selected}
      draggable
      onDragStart={e => {
        if (!node.fileId) return;
        e.dataTransfer.setData('text/plain', node.fileId);
        e.dataTransfer.effectAllowed = 'copy';
        ctx.onDragFile(node.fileId);
      }}
      onContextMenu={e => {
        e.preventDefault();
        ctx.onContextMenu({ file: node, x: e.clientX, y: e.clientY });
      }}
      title={node.name}
    >
      <RowLead>
        <Caret $open={false} />
        <FileOutlined />
        <RowName ellipsis={{ tooltip: node.name }}>{node.name}</RowName>
      </RowLead>
      <RowExtras node={node} />
      {role ? (
        <RoleTag $role={role}>{role.toUpperCase()}</RoleTag>
      ) : (
        <SideAction
          onClick={e => {
            e.stopPropagation();
            ctx.onOpenToSide(node);
          }}
        >
          to side
        </SideAction>
      )}
    </FileRow>
  );
};

const Explorer: React.FC<IExplorerProps> = ({
  nodes,
  openRoles,
  compareSelectId,
  defaultExpanded = true,
  tagFilterEnabled = false,
  tagFilterShowMoreText = 'Show more',
  tagFilterShowLessText = 'Show less',
  onOpenToSide,
  onContextMenu,
  onDragFile,
}) => {
  const [query, setQuery] = useState('');
  const [disabledTags, setDisabledTags] = useState<Set<string>>(new Set());
  const tags = useMemo(() => collectTags(nodes), [nodes]);

  const toggleTag = (label: string) =>
    setDisabledTags(prev => {
      const next = new Set(prev);
      if (!next.delete(label)) next.add(label);
      return next;
    });
  const ctx: IRowCtx = {
    query: query.trim().toLowerCase(),
    openRoles,
    compareSelectId,
    defaultExpanded,
    enabledTags: tagFilterEnabled ? getEnabledTags(tags, disabledTags) : null,
    onOpenToSide,
    onContextMenu,
    onDragFile,
  };

  return (
    <Wrapper>
      <SearchWrap>
        <Search
          placeholder="Search files…"
          allowClear
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {tagFilterEnabled && tags.length > 0 && (
          <TagFilter
            tags={tags}
            disabled={disabledTags}
            showMoreText={tagFilterShowMoreText}
            showLessText={tagFilterShowLessText}
            onToggle={toggleTag}
            onEnableAll={() => setDisabledTags(new Set())}
            onDisableAll={() =>
              setDisabledTags(new Set(tags.map(tag => tag.label)))
            }
          />
        )}
      </SearchWrap>
      <TreeScroll>
        {nodes.map(n => (
          <RootItem key={n.id}>
            <NodeRow node={n} ctx={ctx} />
          </RootItem>
        ))}
      </TreeScroll>
    </Wrapper>
  );
};

export default Explorer;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
`;

const SearchWrap = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const TreeScroll = styled.div`
  flex: 1;
  overflow: auto;
`;

const RootItem = styled.div`
  padding: ${({ theme }) => theme.spacing.xs} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 4px ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.cardHover};
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  width: 12px;
  flex-shrink: 0;
  font-size: 10px;
  transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg);
  transition: transform 0.12s ease;
`;

const RowLead = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  min-height: ${TAG_HEIGHT};
`;

const RowName = styled(Text)`
  flex: 1;
  min-width: 0;
`;

const Tags = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  gap: 4px;
`;

const TagsLine = styled.span`
  display: flex;
  justify-content: flex-end;
  gap: 4px;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

const Children = styled.div`
  padding-left: 16px;
`;

const FileRow = styled(Row)<{ $role?: 'a' | 'b' | 'ref'; $selected?: boolean }>`
  ${({ $role, theme }) =>
    $role &&
    css`
      background: ${theme.colors.primaryLighter};
    `}
  ${({ $selected, theme }) =>
    $selected &&
    css`
      outline: 1px dashed ${theme.colors.primary};
      outline-offset: -1px;
    `}

  .side-action {
    visibility: hidden;
  }
  &:hover .side-action {
    visibility: visible;
  }
`;

const RoleTag = styled.span<{ $role: 'a' | 'b' | 'ref' }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: 0 5px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.primaryActive};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const SideAction = styled.button.attrs({ className: 'side-action' })`
  flex-shrink: 0;
  visibility: hidden;
  border: none;
  background: transparent;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Counter = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-family: ${({ theme }) => theme.typography.mono};
  padding: 0 5px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gray[100]};
  color: ${({ theme }) => theme.colors.gray[500]};
`;
